import React, { useMemo, useState } from 'react';
import {
  Button,
  Form,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { type BarnaSamuha } from '../api/barnaSamuhaApi';
import {
  createBartikPadachhedBarnaSamuhaLink,
  deleteBartikPadachhedBarnaSamuhaLink,
  updateBartikPadachhedBarnaSamuhaLink,
  type BartikPadachhedBarnaSamuhaLink,
} from '../api/ashtadhyayiBartikPadachhedBarnaSamuhaApi';
import type { BartikPadachhedLinkLookupData } from './BartikPadachhedPratyaharLinksPanel';

type LinkFormValues = {
  barnaSamuhaId: number;
};

function errMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || 'Request failed';
}

type Props = BartikPadachhedLinkLookupData & {
  bartikPadachhedId: number;
  links: BartikPadachhedBarnaSamuhaLink[];
  barnaSamuhaOptions: BarnaSamuha[];
  onChanged: () => Promise<void>;
};

export function BartikPadachhedBarnaSamuhaLinksPanel({
  bartikPadachhedId,
  links,
  onChanged,
  barnaSamuhaOptions,
  lookupLoading,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BartikPadachhedBarnaSamuhaLink | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<LinkFormValues>();

  const options = useMemo(
    () =>
      barnaSamuhaOptions.map((p) => ({
        value: p.id,
        label: `${p.name} (#${p.id})`,
        disabled: links.some((l) => l.barnaSamuhaId === p.id && l.id !== editing?.id),
      })),
    [barnaSamuhaOptions, links, editing?.id]
  );

  const columns: ColumnsType<BartikPadachhedBarnaSamuhaLink> = [
    {
      title: 'Barna samuha',
      render: (_, link) => {
        const name = link.barnaSamuha?.name ?? barnaSamuhaOptions.find((p) => p.id === link.barnaSamuhaId)?.name;
        return name ? `${name} (#${link.barnaSamuhaId})` : `#${link.barnaSamuhaId}`;
      },
    },
    {
      title: 'Selected barnas',
      render: (_, link) =>
        link.selectedBarnas.length > 0 ? `${link.selectedBarnas.length} selected` : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Actions',
      width: 120,
      render: (_, link) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditing(link); form.setFieldsValue({ barnaSamuhaId: link.barnaSamuhaId }); setModalOpen(true); }}>
            Edit
          </Button>
          <Popconfirm title="Delete this link?" onConfirm={() => void deleteBartikPadachhedBarnaSamuhaLink(link.id).then(onChanged)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} disabled={lookupLoading}>
        Add link
      </Button>
      <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={links} locale={{ emptyText: 'No barna samuha links' }} />
      <Modal title={editing ? `Edit link #${editing.id}` : 'Add barna samuha link'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields(); }} onOk={() => void (async () => {
        try {
          const values = await form.validateFields();
          setSaving(true);
          if (editing) {
            if (values.barnaSamuhaId === editing.barnaSamuhaId) {
              message.info('No changes to save');
            } else {
              await updateBartikPadachhedBarnaSamuhaLink(editing.id, {
                selectedBarnas:
                  barnaSamuhaOptions.find((b) => b.id === values.barnaSamuhaId)?.associatedBarnas ?? [],
              });
              message.success('Link updated');
            }
          } else {
            const selectedBarnas = barnaSamuhaOptions.find((b) => b.id === values.barnaSamuhaId)?.associatedBarnas ?? [];
            await createBartikPadachhedBarnaSamuhaLink({
              ashtadhyayiBartikPadachhedId: bartikPadachhedId,
              barnaSamuhaId: values.barnaSamuhaId,
              selectedBarnas,
            });
            message.success('Link created');
          }
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
          await onChanged();
        } catch (err: unknown) {
          if ((err as { errorFields?: unknown }).errorFields) return;
          message.error(errMessage(err));
        } finally {
          setSaving(false);
        }
      })()} confirmLoading={saving} destroyOnClose width={560}>
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="barnaSamuhaId" label="Barna samuha" rules={[{ required: true, message: 'Select a barna samuha' }]}>
            <Select showSearch optionFilterProp="label" placeholder="Select barna samuha" options={options} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
